import React, { useState, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { MapPin, Calendar, Clock, Search, ChevronDown } from 'lucide-react';

/**
 * Client Availability Search — mandatory entry point on the marketplace.
 * Users pick a location + date/time window before browsing scooters. The
 * chosen values are lifted to the parent component which fetches the
 * filtered `/api/services` and persists them in the URL.
 */

const HOURS = Array.from({ length: 24 }, (_, h) =>
  ['00', '30'].map((m) => `${String(h).padStart(2, '0')}:${m}`)
).flat();

const todayISO = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.toISOString().split('T')[0];
};

const tomorrowISO = () => {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  d.setHours(0, 0, 0, 0);
  return d.toISOString().split('T')[0];
};

export const AvailabilitySearchBar = ({ zones = [], defaults = {}, onSubmit, onBrowseAll, compact = false, variant = 'card' }) => {
  // `bar`: the compact horizontal desktop version (same fields, same behaviour, same test ids).
  const bar = variant === 'bar';
  const { t } = useTranslation();
  const minDate = useMemo(() => todayISO(), []);
  const [location, setLocation] = useState(defaults.location || '');
  const [startDate, setStartDate] = useState(defaults.startDate ?? '');
  const [endDate, setEndDate] = useState(defaults.endDate ?? '');
  const [startTime, setStartTime] = useState(defaults.startTime || '12:00');
  const [endTime, setEndTime] = useState(defaults.endTime || '12:00');
  const [error, setError] = useState('');

  const hasAnyDate = !!(startDate || endDate || startTime || endTime);

  const isRangeValid = useMemo(() => {
    if (!startDate || !endDate) return false;
    if (endDate < startDate) return false;
    if (startDate === endDate && endTime <= startTime) return false;
    return true;
  }, [startDate, endDate, startTime, endTime]);

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    // No dates entered at all → browse mode with optional zone filter
    if (!hasAnyDate) {
      setError('');
      if (location) {
        // Zone-only search: navigate with zone filter
        onSubmit?.({ location, startDate: '', endDate: '', startTime: '', endTime: '' });
      } else {
        // No zone, no dates → full browse
        onBrowseAll?.();
      }
      return;
    }
    if (!isRangeValid) {
      setError(t('scooters.searchBar.errorInvalidRange'));
      return;
    }
    setError('');
    onSubmit?.({ location, startDate, endDate, startTime: startTime || '12:00', endTime: endTime || '12:00' });
  };

  const handleClear = () => {
    setLocation('');
    setStartDate('');
    setEndDate('');
    setStartTime('');
    setEndTime('');
    setError('');
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={bar
        ? 'bg-white rounded-xl shadow-sm border border-slate-200 p-3 [&_select]:py-2 [&_input]:py-2 [&_label]:mb-1'
        : `bg-white rounded-2xl shadow-lg border border-slate-200 ${compact ? 'p-3' : 'p-4 sm:p-5'}`}
      data-testid="availability-search-form"
    >
      <div className={bar ? 'grid grid-cols-[minmax(0,1.4fr)_repeat(4,minmax(0,1fr))] gap-2.5 items-end' : 'grid grid-cols-1 md:grid-cols-6 gap-3 items-end'}>
        {/* Location */}
        <div className={bar ? '' : 'md:col-span-2'}>
          <label className="text-xs font-semibold text-primary flex items-center gap-1.5 mb-1.5">
            <MapPin className="w-3.5 h-3.5 text-accent" /> {t('scooters.searchBar.zoneLabel')}
          </label>
          <div className="relative">
            <select
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              className="w-full appearance-none bg-background-soft border border-slate-200 rounded-xl px-3 py-2.5 pr-8 text-sm font-medium text-primary focus:border-accent focus:outline-none"
              data-testid="search-location"
            >
              <option value="">{t('scooters.searchBar.allZones')}</option>
              {zones.map((z) => (
                <option key={z} value={z}>{z}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
        </div>

        {/* Start date + time */}
        <div>
          <label className="text-xs font-semibold text-primary flex items-center gap-1.5 mb-1.5">
            <Calendar className="w-3.5 h-3.5 text-accent" /> {t('scooters.searchBar.pickupLabel')}
          </label>
          <input
            type="date"
            value={startDate}
            min={minDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="w-full bg-background-soft border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-primary focus:border-accent focus:outline-none"
            data-testid="search-start-date"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-primary flex items-center gap-1.5 mb-1.5">
            <Clock className="w-3.5 h-3.5 text-accent" /> {t('scooters.searchBar.hourLabel')}
          </label>
          <div className="relative">
            <select
              value={startTime}
              onChange={(e) => setStartTime(e.target.value)}
              className="w-full appearance-none bg-background-soft border border-slate-200 rounded-xl px-3 py-2.5 pr-8 text-sm text-primary focus:border-accent focus:outline-none"
              data-testid="search-start-time"
            >
              {HOURS.map((h) => <option key={h} value={h}>{h}</option>)}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
        </div>

        {/* End date + time */}
        <div>
          <label className="text-xs font-semibold text-primary flex items-center gap-1.5 mb-1.5">
            <Calendar className="w-3.5 h-3.5 text-accent" /> {t('scooters.searchBar.returnLabel')}
          </label>
          <input
            type="date"
            value={endDate}
            min={startDate || minDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="w-full bg-background-soft border border-slate-200 rounded-xl px-3 py-2.5 text-sm text-primary focus:border-accent focus:outline-none"
            data-testid="search-end-date"
          />
        </div>
        <div>
          <label className="text-xs font-semibold text-primary flex items-center gap-1.5 mb-1.5">
            <Clock className="w-3.5 h-3.5 text-accent" /> {t('scooters.searchBar.hourLabel')}
          </label>
          <div className="relative">
            <select
              value={endTime}
              onChange={(e) => setEndTime(e.target.value)}
              className="w-full appearance-none bg-background-soft border border-slate-200 rounded-xl px-3 py-2.5 pr-8 text-sm text-primary focus:border-accent focus:outline-none"
              data-testid="search-end-time"
            >
              {HOURS.map((h) => <option key={h} value={h}>{h}</option>)}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
          </div>
        </div>
      </div>

      <div className={`${bar ? 'mt-2' : 'mt-4'} flex items-center justify-between gap-3 flex-wrap`}>
        <p className="text-xs text-ink-muted flex-1 min-w-[180px]">
          <Trans i18nKey="scooters.searchBar.hint" components={{ strong: <strong /> }} />
        </p>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={handleClear}
            className="text-xs font-semibold rounded-full border border-slate-200 hover:border-accent px-3 py-2 text-primary bg-white"
            data-testid="search-clear"
          >
            {t('scooters.searchBar.clearButton')}
          </button>
          {onBrowseAll && (
            <button
              type="button"
              onClick={onBrowseAll}
              className="text-xs font-semibold rounded-full border border-slate-300 hover:border-accent px-3 py-2 text-primary bg-white"
              data-testid="search-browse-all"
            >
              {t('scooters.searchBar.browseAllButton')}
            </button>
          )}
          <button
            type="submit"
            className="btn-primary inline-flex items-center gap-2"
            data-testid="search-submit"
          >
            <Search className="w-4 h-4" />
            {t('scooters.searchBar.searchButton')}
          </button>
        </div>
      </div>

      {error && (
        <p className="text-xs text-red-600 mt-2" data-testid="search-error">{error}</p>
      )}
    </form>
  );
};
